import { Fragment, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash';
import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { endpoints, withSideload } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { KeyboardShortcutChip } from '@rossum/components/keyboard-shortcut/KeyboardShortcutButton';
import { IconPlayerPlay, IconSearch } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { useSchema } from '../../business/schema/useSchema';
import { useUsers } from '../../business/users/useUsers';
import { useAccessibleFeatures } from '../../components/NavBar/hooks/useAccessibleFeatures';
import { useVisibleTabs } from '../../components/NavBar/hooks/useVisibleTabs';
import {
  tabToLink,
  visibleMenuItemsForTab,
} from '../../components/NavBar/navigationStructure';
import { useApiClient } from '../../lib/apiClient';
import {
  isUserAdmin,
  userRoleNameSelector,
} from '../../redux/modules/user/selectors';
import { useEventCallback } from '../../utils/hooks/useEventCallback';
import { DISALLOWED_STATUSES_FILTER_ITEM } from '../document-list-columns/status/statuses';
import { useHookInterfaces } from '../extension-interop/hooks/useHookInterfaces';
import { useInvokeCommand } from '../extension-interop/hooks/useInvokeCommand';
import { useHooks } from '../extensions/hooks/useHooks';
import { extensionDetailPath } from '../extensions/routes';
import { encodeMqlQuery } from '../mql/encodeMqlQuery';
import { ACCESSIBLE_ANNOTATIONS_FILTER_ITEM } from '../pricing/utils';
import { useVisibleQueueSettingsPages } from '../queue-settings/hooks/useVisibleQueueSettingsPages';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { getFlattenSchemaFields } from '../schemas/getFlattenSchemaFields';

export const QuickSearchAutocomplete = ({
  onClose,
  queue: currentQueue,
}: {
  onClose: () => void;
  queue: Queue | undefined;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedQueueChip, setSelectedQueueChip] = useState<Queue | null>(
    null
  );
  const [highlightedOption, setHighlightedOption] = useState<{
    type: string;
    id: number | string;
  } | null>(null);
  const filteredOptionsRef = useRef<
    Array<{ type: string; id: number | string }>
  >([]);

  const effectiveQueue = selectedQueueChip ?? currentQueue;

  const intl = useIntl();

  const debouncedSetSearchValue = useMemo(
    () => throttle(value => setSearchValue(value), 1000),
    []
  );

  const { push } = useHistory();

  const { allWorkspacesWithQueues, isLoading: queuesLoading } =
    useWorkspacesWithQueues({
      enableQueries: true,
      hideDisabledQueues: true,
    });

  const isAdmin = useSelector(isUserAdmin);

  const { data: users, isFetching: usersLoading } = useUsers(
    { search: searchValue },
    { enabled: isAdmin }
  );

  const { data: hooks, isFetching: hooksLoading } = useHooks(
    { name: searchValue },
    { enabled: isAdmin }
  );

  const features = useAccessibleFeatures();
  const role = useSelector(userRoleNameSelector);
  const tabs = useVisibleTabs();
  const visibleQueueSettingsPages = useVisibleQueueSettingsPages();

  const queuePages = useMemo(
    () =>
      effectiveQueue && isAdmin
        ? visibleQueueSettingsPages
            .filter(p => p !== 'schema')
            .map(page => ({
              name: intl.formatMessage({
                id: `containers.settings.queues.queue.${page}`,
              }),
              type: 'page' as const,
              secondaryInfo: `${intl.formatMessage({
                id: 'features.routes.pageTitles.settings',
              })} > ${intl.formatMessage({
                id: 'features.queueSettings.breadcrumbs.queues',
              })}`,
              id: `settings-queue-${page}`,
              url: undefined,
              frontendUrl: `/queues/${effectiveQueue.id}/settings/${page}`,
            }))
        : [],
    [effectiveQueue, intl, isAdmin, visibleQueueSettingsPages]
  );

  const mainNavigationPages = useMemo(
    () =>
      tabs.flatMap(tab => [
        {
          name: intl.formatMessage({
            id: `components.appBar.${tab.name}`,
          }),
          type: 'page' as const,
          secondaryInfo: undefined,
          id: `${tab.name}`,
          url: undefined,
          frontendUrl: tab.link,
        },
        ...visibleMenuItemsForTab(tab.menu ?? [], features, role).map(item => ({
          name: intl.formatMessage({
            id: `components.appBar.menu.${item.translationKey}`,
          }),
          type: 'page' as const,
          secondaryInfo: intl.formatMessage({
            id: `components.appBar.${tab.name}`,
          }),
          id: `${tab.name}-${item.name}`,
          url: undefined,
          frontendUrl: item.link,
        })),
      ]),
    [features, intl, role, tabs]
  );

  const { data: commands, isFetching: commandsLoading } = useHookInterfaces({
    name: searchValue || undefined,
    type: 'command',
    canInvoke: true,
  });

  const { data: schema, isFetching: schemaLoading } = useSchema(
    effectiveQueue?.schema
  );

  const schemaFields = useMemo(
    () =>
      isAdmin
        ? Object.values(getFlattenSchemaFields(schema?.content ?? [])).flatMap(
            ({ path, field }) =>
              path !== null && field !== null ? [{ ...field, path }] : []
          )
        : [],
    [isAdmin, schema?.content]
  );

  const mqlQuery = encodeMqlQuery([
    ACCESSIBLE_ANNOTATIONS_FILTER_ITEM,
    DISALLOWED_STATUSES_FILTER_ITEM,
  ]);

  const { mutate: invokeCommand, isLoading: isInvoking } = useInvokeCommand();

  const api = useApiClient();
  const { data: annotations, isFetching: annotationsLoading } = useQuery(
    ['quick-search-annotations', searchValue] as const,
    () =>
      api.request(
        withSideload(
          endpoints.annotations.search(
            {},
            { query: mqlQuery, queryString: { string: searchValue } }
          ),
          { documents: true, queues: true, workspaces: true }
        )
      ),
    { enabled: searchValue.length > 0, keepPreviousData: true }
  );

  const isLoading =
    inputValue &&
    (annotationsLoading ||
      queuesLoading ||
      hooksLoading ||
      usersLoading ||
      schemaLoading ||
      commandsLoading ||
      inputValue !== searchValue);

  const options = useMemo(() => {
    const queueOptions =
      allWorkspacesWithQueues?.flatMap(workspace =>
        workspace.queues.map(queue => ({
          name: `${workspace.name} > ${queue.name}`,
          type: 'queue' as const,
          secondaryInfo: undefined,
          id: queue.id,
          url: queue.url,
          frontendUrl: `/queues/${queue.id}`,
        }))
      ) ?? [];

    const userOptions =
      users?.results.map(user => ({
        name: `${user.firstName} ${user.lastName}`,
        type: 'user' as const,
        secondaryInfo: user.email,
        id: user.id,
        url: user.url,
        frontendUrl: `/settings/users/${user.id}`,
      })) ?? [];

    const hookOptions =
      hooks?.results.map(hook => ({
        name: `${hook.name}`,
        type: 'extension' as const,
        secondaryInfo: undefined,
        id: hook.id,
        url: hook.url,
        frontendUrl: extensionDetailPath(hook.id),
      })) ?? [];

    const fieldOptions =
      effectiveQueue && schemaFields
        ? schemaFields.map(field => ({
            name: `${field.label} (${field.id})`,
            type: 'field' as const,
            secondaryInfo: field.path.map(elem => elem.label).join(' > '),
            id: field.id,
            url: undefined,
            frontendUrl: `/queues/${effectiveQueue.id}/settings/fields/${field.path.map(elem => elem.id).join('/')}`,
          }))
        : [];

    const annotationOptions =
      annotations?.results?.map(annotation => {
        const queue = annotations?.queues.find(
          queue => queue.url === annotation.queue
        );
        const workspace = annotations?.workspaces.find(
          workspace => workspace.url === queue?.workspace
        );
        const document = annotations?.documents.find(
          document => document.url === annotation.document
        );

        return {
          name: `${document?.originalFileName}`,
          type: 'document' as const,
          secondaryInfo: `${workspace?.name} > ${queue?.name}`,
          id: annotation.id,
          url: annotation.url,
          frontendUrl: `/document/${annotation.id}`,
        };
      }) ?? [];

    const commandOptions = commands
      ? commands.results.map(
          command =>
            ({
              type: 'command' as const,
              name: command.name,
              secondaryInfo: command.description,
              id: command.id,
              url: command.url,
              frontendUrl: undefined,
            }) as const
        )
      : [];

    if (inputValue) {
      return [
        ...queuePages,
        ...mainNavigationPages,
        ...commandOptions,
        ...fieldOptions,
        ...userOptions,
        ...queueOptions,
        ...hookOptions,
        ...annotationOptions,
      ];
    }

    if (effectiveQueue) {
      return [...queuePages, ...commandOptions];
    }

    return [...commandOptions];
  }, [
    allWorkspacesWithQueues,
    users?.results,
    hooks?.results,
    effectiveQueue,
    schemaFields,
    annotations?.results,
    annotations?.queues,
    annotations?.workspaces,
    annotations?.documents,
    commands,
    inputValue,
    queuePages,
    mainNavigationPages,
  ]);

  const handleKeyDown = useEventCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      muiHandler?: React.KeyboardEventHandler<HTMLInputElement>
    ) => {
      if (e.key === 'Backspace' && inputValue === '' && selectedQueueChip) {
        setSelectedQueueChip(null);
        return;
      }

      // onHighlightChange doesn't fire for the initial autoHighlight,
      // so fall back to the first visible option in that case.
      const effective =
        highlightedOption ?? filteredOptionsRef.current[0] ?? null;

      if (e.key === 'Tab' && effective?.type === 'queue') {
        e.preventDefault();
        const queue = allWorkspacesWithQueues
          ?.flatMap(w => w.queues)
          .find(q => q.id === effective.id);
        if (queue) {
          setSelectedQueueChip(queue);
          setInputValue('');
          setSearchValue('');
        }
        return;
      }

      muiHandler?.(e);
    }
  );

  return (
    <Autocomplete
      inputValue={inputValue}
      onInputChange={(_, v) => {
        setSearchValue('');
        debouncedSetSearchValue(v);
        setInputValue(v);
        // It is necessary to clear the highlight, because otherwise it would override the "first selected option" after the next search.
        setHighlightedOption(null);
      }}
      open
      autoHighlight
      onClose={(_, reason) => {
        if (reason === 'escape') {
          onClose();
        }
      }}
      onHighlightChange={(_, option) => {
        setHighlightedOption(
          option ? { type: option.type, id: option.id } : null
        );
      }}
      fullWidth
      sx={{ width: 500 }}
      options={options}
      renderInput={field => (
        <TextField
          {...field}
          inputProps={{
            ...field.inputProps,
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(e, field.inputProps?.onKeyDown),
          }}
          InputProps={{
            ...field.InputProps,
            startAdornment: (
              <>
                <SvgIcon fontSize="medium">
                  <IconSearch />
                </SvgIcon>
                {selectedQueueChip && (
                  <Chip
                    label={selectedQueueChip.name}
                    size="small"
                    onDelete={() => setSelectedQueueChip(null)}
                    sx={{ ml: 0.5 }}
                  />
                )}
              </>
            ),
            endAdornment: isLoading ? <CircularProgress size={25} /> : null,
          }}
          autoFocus
        />
      )}
      groupBy={option => option.type}
      renderGroup={({ group, key, children }) =>
        group === 'queue' ||
        group === 'extension' ||
        group === 'document' ||
        group === 'user' ||
        group === 'command' ||
        group === 'page' ||
        group === 'field' ? (
          <Fragment key={key}>
            <ListItemText
              primary={intl.formatMessage({
                id: `features.quickSearch.type.${group}`,
              })}
              primaryTypographyProps={{ fontWeight: 'bold' }}
              sx={{ px: 2 }}
            />
            {children}
          </Fragment>
        ) : null
      }
      renderOption={(props, option) => {
        const effectiveHighlight =
          highlightedOption ?? filteredOptionsRef.current[0];
        const isHighlightedQueue =
          option.type === 'queue' &&
          effectiveHighlight?.type === 'queue' &&
          option.id === effectiveHighlight?.id;

        return (
          <ListItem {...props} key={`${option.type}-${option.id}`}>
            {option.type === 'command' && !isInvoking ? (
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="large">
                  <IconPlayerPlay />
                </SvgIcon>
              </ListItemIcon>
            ) : null}
            {option.type === 'command' && isInvoking ? (
              <ListItemIcon>
                <CircularProgress size={18} />
              </ListItemIcon>
            ) : null}
            <ListItemText
              primary={option.name}
              secondary={option.secondaryInfo}
              primaryTypographyProps={{ variant: 'body2' }}
            />
            {isHighlightedQueue && (
              <Typography variant="caption">
                <KeyboardShortcutChip value="Tab ↹" />
              </Typography>
            )}
          </ListItem>
        );
      }}
      getOptionLabel={option => option.name}
      noOptionsText={
        inputValue
          ? intl.formatMessage({ id: 'features.quickSearch.noResultsText' })
          : intl.formatMessage({ id: 'features.quickSearch.initialText' })
      }
      filterOptions={(filterOptions, state) => {
        const [frontendFiltered, backendFiltered] = R.partition(
          filterOptions,
          option =>
            option.type === 'queue' ||
            option.type === 'field' ||
            option.type === 'page'
        );

        const normalizedInputValue = state.inputValue.trim();

        const filtered = matchSorter(frontendFiltered, normalizedInputValue, {
          keys: [
            {
              threshold: matchSorter.rankings.CONTAINS,
              key: option => option.name,
            },
            {
              threshold: matchSorter.rankings.EQUAL,
              key: option => option.id.toString(),
            },
          ],
        });

        const result = [...R.sortBy(filtered, o => o.type), ...backendFiltered];
        filteredOptionsRef.current = result;
        return result;
      }}
      onChange={(_, v) => {
        if (v && v.frontendUrl) {
          push(tabToLink({ link: v.frontendUrl }));
          onClose();
        }
        if (v && v.type === 'command') {
          invokeCommand(
            { hookInterface: v.url, payload: {} },
            {
              onSuccess: () => {
                onClose();
              },
            }
          );
        }
      }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
          },
        },
      }}
    />
  );
};
